import React, { useState } from 'react';
import {
  SienaguardServiceDetail,
  ContactMailResponse,
  addContactMail,
  removeContactMail
} from '../../../src/api/sienaguardService';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt } from 'react-icons/fa';

/** Formato de fechas: YYYY-MM-DD -> DD/MM/YYYY */
function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

interface ServiceDetailsDrawerProps {
  service: SienaguardServiceDetail | null;
  onClose: () => void;
  onRefresh: () => void;
  onNotify: (message: string) => void;
}

const ServiceDetailsDrawer: React.FC<ServiceDetailsDrawerProps> = ({
  service,
  onClose,
  onRefresh,
  onNotify
}) => {
  const { t } = useTranslation();
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);

  if (!service) return null;

  // Añadir correo
  const handleAddEmail = async () => {
    setLoading(true);
    try {
      await addContactMail(service.id, newEmail);
      setNewEmail('');
      onRefresh();
      onNotify('¡Correo añadido con éxito!');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Eliminar correo
  const handleDeleteEmail = async (contactId: number) => {
    setLoading(true);
    try {
      await removeContactMail(contactId);
      onRefresh();
      onNotify('¡Correo eliminado con éxito!');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const noEmails = !service.contact_mails || service.contact_mails.length === 0;

  // Fechas en DD/MM/YYYY
  const startDate = formatDate(service.start_date);
  const endDate = formatDate(service.end_date);

  return (
    <>
      {/* Overlay (clic fuera cierra) */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      ></div>

      {/* Drawer estilo ciber */}
      <div
        className="fixed inset-y-0 right-0 w-[36rem] h-screen 
                  bg-gradient-to-b from-[#0A1723] to-[#050C12] 
                  text-white shadow-lg z-50 flex flex-col 
                  transform transition-transform"
        onClick={e => e.stopPropagation()}
      >
        {/* Header con "Volver" arriba y Título centrado abajo */}
        <div className="border-b border-[#14202E] p-4 flex flex-col gap-2">
          <button
            onClick={onClose}
            className="text-[#00FF69] hover:underline text-m font-semibold self-start"
          >
            ← {t('serviceDrawer.back')}
          </button>
          <h2 className="text-lg font-bold text-center">
            {t('serviceDrawer.serviceDetails')}
          </h2>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Información básica */}
          <div>
            <p>
              <strong>{t('serviceDrawer.domain')}:</strong> {service.domain}
            </p>
            <p>
              <strong>{t('serviceDrawer.startDate')}:</strong> {startDate}
            </p>
            <p>
              <strong>{t('serviceDrawer.endDate')}:</strong> {endDate}
            </p>
          </div>

          {/* Correos de contacto */}
          <div>
            <h3 className="text-md font-semibold mb-2">
              {t('serviceDrawer.contactEmails')}
            </h3>

            {noEmails ? (
              <p className="text-red-400 text-sm">
                {t('serviceDrawer.noContactEmails')}<br />
                {t('serviceDrawer.recommendAddEmail')}
              </p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-white">
                    <th className="p-2 text-left">{t('serviceDrawer.email')}</th>
                    <th className="p-2 text-center">
                      {t('serviceDrawer.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {service.contact_mails?.map((mail: ContactMailResponse) => (
                    <tr key={mail.id} className="border-b border-gray-500">
                      <td className="p-2">{mail.email}</td>
                      <td className="p-2 text-center">
                        <button
                          onClick={() => handleDeleteEmail(mail.id)}
                          className="text-red-400 hover:text-red-500"
                          title={t('serviceDrawer.delete') as string}
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Espacio extra antes del input */}
            <div className="mt-4 flex gap-2 items-center">
              <input
                type="email"
                placeholder="Nuevo correo"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 flex-1 text-black"
              />
              <button
                onClick={handleAddEmail}
                disabled={loading || !newEmail}
                className="bg-[#00FF69] text-black px-3 py-1 rounded 
                           hover:bg-[#00e859] transition-colors font-semibold"
              >
                {loading
                  ? t('serviceDrawer.addingEmail', 'Agregando...')
                  : t('serviceDrawer.addEmail', 'Agregar')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceDetailsDrawer;
